"use client";

import * as React from "react";
import {
  Box,
  Button,
  Link,
  Paper,
  Typography,
  Container,
  Slide,
} from "@mui/material";
import Clarity from "@microsoft/clarity";
import NextLink from "next/link";

// The GoogleAnalytics component from @next/third-parties/google will handle loading gtag.js
// We still declare it here for TypeScript type safety when we call it.
declare global {
  interface Window {
    gtag: (param1: string, param2: string, param3: object) => void;
  }
}

export default function CookieConsentBanner() {
  // State to control the visibility of the banner
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    // Check if the user has already made a choice
    const consent = localStorage.getItem("cookie_consent");

    // If no choice has been made, show the banner
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookie_consent", "accepted");
    setVisible(false);

    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
      });
      Clarity.consent(true);
    }
  };

  const handleDecline = () => {
    localStorage.setItem("cookie_consent", "declined");
    setVisible(false);

    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
      });
      Clarity.consent(false);
    }
  };

  // If the component is not visible, render nothing.
  if (!visible) {
    return null;
  }

  return (
    // Use Slide transition for a smooth entrance
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      {/* This Box is the fixed container at the bottom of the viewport */}
      <Box
        component={Paper}
        square
        elevation={0}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          // Use theme's zIndex to ensure it's on top of other content
          zIndex: (theme) => theme.zIndex.modal,
          // Use theme-aware colors that adapt to light/dark mode
          backgroundColor: "background.paper",
          borderTop: 2,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              py: 2,
              px: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              variant="body2"
              sx={{ textAlign: { xs: "center", sm: "left" }, margin: 0 }}
            >
              Cookies are used to enhance your browsing experience and analyze
              traffic. Read our{" "}
              <Link
                component={NextLink}
                href="/privacy-policy"
                sx={{ ml: 0, fontWeight: "medium" }}
              >
                privacy policy
              </Link>{" "}
              to learn more.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleDecline}
                sx={{ color: "text.secondary" }}
              >
                Decline
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={handleAccept}
              >
                Accept
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Slide>
  );
}
